import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Trophy, Check, Lock, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { achievementsApi } from '../services/api';

const AchievementsPage = () => {
  const { language } = useLanguage();
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAchievements();
  }, []);

  const fetchAchievements = async () => {
    try {
      const data = await achievementsApi.getAll();
      setAchievements(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalPoints = achievements
    .filter(a => a.unlocked)
    .reduce((sum, a) => sum + a.points, 0);

  const unlockedCount = achievements.filter(a => a.unlocked).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d1f1f] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d1f1f]">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center space-x-4 mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-6 h-6" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-white">
              {language === 'en' ? 'Achievements' : 'Logros'}
            </h1>
            <p className="text-gray-400">
              {language === 'en' ? 'Your journey milestones' : 'Los hitos de tu camino'}
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-2xl p-6 border border-yellow-500/30">
            <div className="flex items-center space-x-3 mb-2">
              <Trophy className="w-8 h-8 text-yellow-400" />
              <span className="text-3xl font-bold text-white">{unlockedCount}/{achievements.length}</span>
            </div>
            <p className="text-gray-400">
              {language === 'en' ? 'Achievements Unlocked' : 'Logros Desbloqueados'}
            </p>
          </div>

          <div className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-2xl p-6 border border-emerald-500/30">
            <div className="flex items-center space-x-3 mb-2">
              <Star className="w-8 h-8 text-emerald-400" />
              <span className="text-3xl font-bold text-white">{totalPoints}</span>
            </div>
            <p className="text-gray-400">
              {language === 'en' ? 'Total Points' : 'Puntos Totales'}
            </p>
          </div>
        </div>

        {/* Achievements Grid */}
        <div className="grid md:grid-cols-2 gap-4">
          {achievements.map((achievement) => (
            <div
              key={achievement.id}
              className={`relative overflow-hidden rounded-2xl p-6 transition-all ${
                achievement.unlocked
                  ? 'bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-2 border-yellow-500/30'
                  : 'bg-gray-900/50 border border-gray-800 opacity-60'
              }`}
            >
              {/* Background glow for unlocked */}
              {achievement.unlocked && (
                <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-500/20 rounded-full blur-3xl" />
              )}

              <div className="relative z-10 flex items-start space-x-4">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
                  achievement.unlocked
                    ? 'bg-gradient-to-br from-yellow-500 to-orange-500'
                    : 'bg-gray-800'
                }`}>
                  {achievement.unlocked ? (
                    <Check className="w-8 h-8 text-white" />
                  ) : (
                    <Lock className="w-8 h-8 text-gray-500" />
                  )}
                </div>

                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className={`font-bold ${achievement.unlocked ? 'text-white' : 'text-gray-400'}`}>
                      {language === 'en' ? achievement.name_en : achievement.name_es}
                    </h3>
                    <span className={`text-sm font-medium ${
                      achievement.unlocked ? 'text-yellow-400' : 'text-gray-500'
                    }`}>
                      {achievement.points} pts
                    </span>
                  </div>
                  <p className={`text-sm ${achievement.unlocked ? 'text-gray-300' : 'text-gray-500'}`}>
                    {language === 'en' ? achievement.description_en : achievement.description_es}
                  </p>
                  {achievement.unlocked && achievement.unlocked_at && (
                    <p className="text-xs text-yellow-400/70 mt-2">
                      {language === 'en' ? 'Unlocked on' : 'Desbloqueado el'}{' '}
                      {new Date(achievement.unlocked_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {achievements.length === 0 && (
          <div className="text-center py-16">
            <Trophy className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <p className="text-gray-500">
              {language === 'en' ? 'No achievements available' : 'No hay logros disponibles'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AchievementsPage;
